export type QrCodeOptions = {
  size?: number;
  alt?: string;
  includeHash?: boolean;      // défaut: false (équivaut à "noIncludeHash = true" avant)
  reportId?: string | number; // défaut: getPageReportId()
  baseUrl?: string | URL;     // défaut: location.href
  className?: string;         // optionnel: classe CSS sur l’<img>
  decoding?: 'sync' | 'async' | 'auto';
  loading?: 'lazy' | 'eager' | 'auto';
};

/** Construit l’URL /result/{reportId}/qrcode.jpg en reprenant l’URL courante */
export function buildQrCodeURL(opts: QrCodeOptions = {}): string {
  const {
    includeHash = false,
    baseUrl = location.href,
  } = opts;

  const url = new URL('qrcode.png', baseUrl.toString());
  if (includeHash) {
    const h = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
    url.searchParams.set('hash', h);
  } else {
    url.searchParams.delete('hash');
  }
  url.hash = '';
  return url.toString();
}

/**
 * Crée (ou met à jour si déjà présent) un <img> QR Code dans `container`.
 * Retourne l’<img>.
 */
export function createQrCode(container: Element | DocumentFragment, opts: QrCodeOptions = {}): HTMLImageElement {
  const imgExisting = container.querySelector('img[data-a4i-qr]') as HTMLImageElement | null;
  const src = buildQrCodeURL(opts);

  const img = imgExisting ?? document.createElement('img');
  img.setAttribute('data-a4i-qr', '1');
  img.src = src;

  // Options visuelles avec valeurs par défaut
  if (opts.size) {
    img.width = opts.size;
    img.height = opts.size;
  }
  img.alt = opts.alt ?? 'QR Code';
  img.decoding = opts.decoding ?? 'async';
  if (opts.className) img.className = opts.className;

  if (!imgExisting) container.appendChild(img);
  return img;
}

/** Utilitaire: lit les options depuis les data-* d’un élément <a4i-qrcode> */
function optionsFromDataset(el: HTMLElement): QrCodeOptions {
  const rawNoInc = el.getAttribute('data-no-include-hash');
  const noIncludeHash = rawNoInc == null ? true : !['false','0','off','no'].includes(rawNoInc.trim().toLowerCase());

  const rawSize = el.getAttribute('data-size');
  const size = rawSize ? parseInt(rawSize, 10) : undefined;

  const alt = el.getAttribute('data-alt') ?? undefined;

  return {
    size,
    alt,
    includeHash: !noIncludeHash,
  };
}

/** Custom Element <a4i-qrcode> basé sur createQrCode() */
export class A4I_QrCode extends HTMLElement {
  connectedCallback() {
    const opts = optionsFromDataset(this);
    createQrCode(this, opts);
  }

  static get observedAttributes() { return ['data-size','data-alt','data-no-include-hash']; }
  attributeChangedCallback() {
    const opts = optionsFromDataset(this);
    createQrCode(this, opts);
  }
}

export function defineA4IQrCode(tagName = 'a4i-qrcode') {
  if (!customElements.get(tagName)) {
    customElements.define(tagName, A4I_QrCode);
  }
}

defineA4IQrCode();
type OpenDialogDetail = { dialog: HTMLDialogElement; content_id: string | number; content: unknown };
document.addEventListener('a4i_openDialog', (e) => {
	const { dialog, content_id } = (e as CustomEvent<OpenDialogDetail>).detail;
	createQrCode(dialog, { size: 200, includeHash: true})
});
